import { useState } from "react";
import { Box, Image } from "@chakra-ui/react";

const idOf = (u) => {
  if (!u) return "";
  if (typeof u === "object") return String(u._id || u.id || "");
  return String(u);
};

const normalizeMember = (u) => {
  if (!u || typeof u !== "object") {
    return { _id: idOf(u), name: "?", pic: "" };
  }
  return { _id: idOf(u), name: u.name || "?", pic: u.pic || "" };
};

// Backend stamps users without uploads with a legacy icon-library.com URL
// (see backend/models/userModel.js). That host is slow/dead, so treat it as
// "no pic" and render a local dummy glyph instead — same rule as mobile
// Avatar, otherwise the tile shows a broken-image placeholder.
const PLACEHOLDER_MATCHERS = ["icon-library.com", "anonymous-avatar"];
const isRealPic = (pic) =>
  typeof pic === "string" &&
  pic.trim().toLowerCase().startsWith("http") &&
  !PLACEHOLDER_MATCHERS.some((h) => pic.toLowerCase().includes(h));

// Same hues as mobile avatarColor (mobile/src/theme/chats.js).
const AVATAR_HUES = [265, 190, 320, 210, 160, 20];
const avatarBg = (name = "?") => {
  const s = String(name || "?");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return `hsl(${AVATAR_HUES[h % AVATAR_HUES.length]} 70% 45%)`;
};

// Offline-proof dummy profile glyph (material "account" path).
const PersonGlyph = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="#fff"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
  </svg>
);

const DummyTile = ({ member, glyphSize }) => (
  <Box
    w="100%"
    h="100%"
    display="flex"
    alignItems="center"
    justifyContent="center"
    bg={avatarBg(member?.name)}
    userSelect="none"
  >
    <PersonGlyph size={glyphSize} />
  </Box>
);

// Single mosaic tile: photo when a real URL exists, dummy glyph otherwise.
// Plain Box+Image (not Chakra Avatar) so the tile stays square and lets the
// circular parent do the clipping — a round Avatar inside would not tile.
// Any load failure swaps to the dummy via onError.
const Cell = ({ member, glyphSize }) => {
  const [failed, setFailed] = useState(false);
  const src =
    !failed && isRealPic(member?.pic) ? member.pic.trim() : null;
  if (!src) return <DummyTile member={member} glyphSize={glyphSize} />;
  return (
    <Image
      src={src}
      alt={member?.name || "Group member"}
      w="100%"
      h="100%"
      objectFit="cover"
      draggable={false}
      onError={() => setFailed(true)}
    />
  );
};

// Messenger-style group avatar: top half is 1 member, bottom half is split
// into 2 members. Falls back to a single photo when the group has a custom
// pic, only 1 member to show, or no member data at all.
const GroupChatAvatar = ({
  users = [],
  name = "Group",
  pic = "",
  size = 32,
  currentUserId = "",
}) => {
  const px = typeof size === "number" ? `${size}px` : size;
  const n = typeof size === "number" ? size : parseInt(size, 10) || 32;
  const label = `Group avatar for ${name || "Group"}`;
  const [picFailed, setPicFailed] = useState(false);
  const custom =
    typeof pic === "string" && pic.trim() && !picFailed
      ? pic.trim()
      : null;

  // A custom group photo always wins over the mosaic; if it fails to load,
  // fall through to the member mosaic instead of a broken image.
  if (custom) {
    return (
      <Box
        w={px}
        h={px}
        minW={px}
        minH={px}
        borderRadius="full"
        overflow="hidden"
        bg="#64748b"
        flexShrink={0}
        title={name}
        aria-label={label}
        role="img"
      >
        <Image
          src={custom}
          alt={name || "Group"}
          w="100%"
          h="100%"
          objectFit="cover"
          draggable={false}
          onError={() => setPicFailed(true)}
        />
      </Box>
    );
  }

  const list = Array.isArray(users) ? users.map(normalizeMember) : [];
  const me = currentUserId ? String(currentUserId) : "";
  // Prefer other members so the mosaic doesn't just show yourself.
  const others = me ? list.filter((u) => u._id !== me) : [...list];
  const self = me ? list.filter((u) => u._id === me) : [];
  const ordered = [...others, ...self].slice(0, 3);

  if (ordered.length === 0) {
    return (
      <Box
        w={px}
        h={px}
        minW={px}
        minH={px}
        borderRadius="full"
        overflow="hidden"
        flexShrink={0}
        title={name}
        aria-label={label}
        role="img"
      >
        <DummyTile member={{ name }} glyphSize={Math.round(n * 0.62)} />
      </Box>
    );
  }

  if (ordered.length === 1) {
    return (
      <Box
        w={px}
        h={px}
        minW={px}
        minH={px}
        borderRadius="full"
        overflow="hidden"
        bg="#fff"
        flexShrink={0}
        title={name}
        aria-label={label}
        role="img"
      >
        <Cell member={ordered[0]} glyphSize={Math.round(n * 0.62)} />
      </Box>
    );
  }

  // Two members: vertical split.
  if (ordered.length === 2) {
    return (
      <Box
        w={px}
        h={px}
        minW={px}
        minH={px}
        borderRadius="full"
        overflow="hidden"
        bg="#fff"
        display="flex"
        flexDir="row"
        flexShrink={0}
        title={name}
        aria-label={label}
        role="img"
      >
        <Box flex="1" h="100%" borderRight="1px solid #fff" overflow="hidden">
          <Cell member={ordered[0]} glyphSize={Math.round(n * 0.38)} />
        </Box>
        <Box flex="1" h="100%" overflow="hidden">
          <Cell member={ordered[1]} glyphSize={Math.round(n * 0.38)} />
        </Box>
      </Box>
    );
  }

  // Three+: 1 member across the top half, 2 members split across the bottom.
  return (
    <Box
      w={px}
      h={px}
      minW={px}
      minH={px}
      borderRadius="full"
      overflow="hidden"
      bg="#fff"
      display="flex"
      flexDir="column"
      flexShrink={0}
      title={name}
      aria-label={label}
      role="img"
    >
      <Box w="100%" h="50%" borderBottom="1px solid #fff" overflow="hidden">
        <Cell member={ordered[0]} glyphSize={Math.round(n * 0.3)} />
      </Box>
      <Box w="100%" h="50%" display="flex" flexDir="row">
        <Box flex="1" h="100%" borderRight="1px solid #fff" overflow="hidden">
          <Cell member={ordered[1]} glyphSize={Math.round(n * 0.26)} />
        </Box>
        <Box flex="1" h="100%" overflow="hidden">
          <Cell member={ordered[2]} glyphSize={Math.round(n * 0.26)} />
        </Box>
      </Box>
    </Box>
  );
};

export default GroupChatAvatar;
